import React, { useCallback, useRef } from 'react'
import type { NumberFieldProps } from './Input.types'

export default function NumberField({
  name,
  value,
  label,
  placeholder,
  min,
  max,
  step = 1,
  required,
  disabled,
  error,
  helperText,
  onChange,
}: NumberFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const hasValue = value !== undefined && value !== ''

  const rootClasses = [
    'rd-input',
    'rd-input--medium',
    'rd-input--floating',
    'rd-input--number',
    error ? 'rd-input--error' : '',
    disabled ? 'rd-input--disabled' : '',
    hasValue ? 'has-value' : '',
  ].filter(Boolean).join(' ')

  const labelClasses = [
    'rd-input__label',
    required ? 'rd-input__label--required' : '',
  ].filter(Boolean).join(' ')

  const dispatchChange = useCallback((newValue: string) => {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype, 'value',
    )?.set
    if (nativeInputValueSetter && inputRef.current) {
      nativeInputValueSetter.call(inputRef.current, newValue)
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }, [])

  const handleArrowUp = useCallback(() => {
    if (disabled) return
    const current = parseFloat(value) || 0
    const next = max !== undefined ? Math.min(current + step, max) : current + step
    dispatchChange(String(next))
  }, [value, step, max, disabled, dispatchChange])

  const handleArrowDown = useCallback(() => {
    if (disabled) return
    const current = parseFloat(value) || 0
    const next = min !== undefined ? Math.max(current - step, min) : current - step
    dispatchChange(String(next))
  }, [value, step, min, disabled, dispatchChange])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) return
    const allowed = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']
    if (allowed.includes(e.key)) return
    const allowMinus = min === undefined || min < 0
    const pattern = allowMinus ? /^[\d.-]$/ : /^[\d.]$/
    if (e.key.length === 1 && !pattern.test(e.key)) {
      e.preventDefault()
    }
  }, [min])

  return (
    <div className={rootClasses}>
      <div className="rd-input__wrapper">
        <input
          ref={inputRef}
          className="rd-input__field"
          type="text"
          inputMode="numeric"
          name={name}
          value={value}
          placeholder={placeholder || ' '}
          required={required}
          disabled={disabled}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
        {label && <label className={labelClasses}>{label}</label>}
        <div className="rd-input__arrows">
          <button
            type="button"
            className="rd-input__arrow rd-icon-arrow-up"
            onClick={handleArrowUp}
            disabled={disabled}
            tabIndex={-1}
            aria-label="Increase"
          />
          <button
            type="button"
            className="rd-input__arrow rd-icon-arrow-down"
            onClick={handleArrowDown}
            disabled={disabled}
            tabIndex={-1}
            aria-label="Decrease"
          />
        </div>
      </div>
      {error && <span className="rd-input__error-text">{error}</span>}
      {helperText && !error && <span className="rd-input__helper-text">{helperText}</span>}
    </div>
  )
}
